/**
 * Presentation for background-upload states — labels, tone, icon, and the copy under each row.
 *
 * Ported field-for-field from the design prototype's `UP` / `UP_TONE` / `upCaption` (see
 * `.claude/Salah Bahzad Teacher Portal/Admin Portal.dc.html`). It lives in the shared UI lib and takes only
 * primitives, because Nx forbids a `type:ui` library from depending on a `type:data-access` one — which is the right
 * split anyway: what a state *looks like* is a UI concern, what it *is* belongs to the store.
 */

export type UploadStatusKey =
  | 'Queued'
  | 'Uploading'
  | 'Retrying'
  | 'Paused'
  | 'Interrupted'
  | 'Finalising'
  | 'Processing'
  | 'Ready'
  | 'Failed';

export type UploadTone = 'info' | 'active' | 'pending' | 'rejected' | 'inactive';

export interface UploadStatusMeta {
  readonly label: string;
  readonly tone: UploadTone;
  readonly icon: string;
  readonly bar: 'primary' | 'success' | 'danger' | 'warning' | 'info';
  /** Draw the row's attention: a state the teacher has to act on. */
  readonly attention?: 'warning' | 'danger';
  /** Spin the glyph — only for states where something is actively being retried. */
  readonly spin?: boolean;
  /** Dim the bar: real progress exists but nothing is moving right now. */
  readonly dim?: boolean;
}

/** Outline glyphs on the 24×24 grid, matching the prototype's icon set. Never filled. */
const ICONS = {
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7.5V12l3 2',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  refresh: 'M20.5 12a8.5 8.5 0 1 1-2.9-6.4M21 4v5h-5',
  pause: 'M10 4.5H6.5v15H10zM17.5 4.5H14v15h3.5z',
  cloudOff: 'M3 3l18 18M20.3 14.4A4 4 0 0 0 18 7.5h-1.3a7 7 0 0 0-9.2-3.6M5.5 8.2A7 7 0 0 0 7 20h11',
  shieldCheck: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 11.5l2.2 2.2 4.3-4.2',
  film: 'M4 4h16v16H4zM4 9.5h16M4 14.5h16M9 4v16M15 4v16',
  checkCircle: 'M21.8 11.1V12a9.8 9.8 0 1 1-5.8-9M22 4.5L12 14.6l-3-3',
  alert: 'M10.3 3.9L1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4.5M12 17.2h.01',
  layers: 'M12 2.5L3 7l9 4.5L21 7zM3 12l9 4.5L21 12M3 17l9 4.5L21 17',
} as const;

export const UPLOAD_STATUS_META: Readonly<Record<UploadStatusKey, UploadStatusMeta>> = {
  Queued: { label: 'Queued', tone: 'inactive', icon: ICONS.clock, bar: 'primary', dim: true },
  Uploading: { label: 'Uploading', tone: 'info', icon: ICONS.upload, bar: 'primary' },
  Retrying: { label: 'Retrying…', tone: 'pending', icon: ICONS.refresh, bar: 'warning', spin: true },
  Paused: { label: 'Paused', tone: 'inactive', icon: ICONS.pause, bar: 'primary', dim: true },
  Interrupted: {
    label: 'Interrupted',
    tone: 'pending',
    icon: ICONS.cloudOff,
    bar: 'warning',
    attention: 'warning',
  },
  Finalising: { label: 'Finalising', tone: 'info', icon: ICONS.shieldCheck, bar: 'primary' },
  Processing: { label: 'Processing', tone: 'info', icon: ICONS.film, bar: 'info' },
  Ready: { label: 'Ready', tone: 'active', icon: ICONS.checkCircle, bar: 'success' },
  Failed: { label: 'Failed', tone: 'rejected', icon: ICONS.alert, bar: 'danger', attention: 'danger' },
};

export const UPLOAD_EMPTY_ICON = ICONS.layers;
export const UPLOAD_TRAY_ICON = ICONS.upload;
export const UPLOAD_ATTENTION_ICON = ICONS.cloudOff;
export const UPLOAD_DONE_ICON = ICONS.checkCircle;

/** Tone → the design system's background / foreground / border triple. No raw colours anywhere. */
export function uploadToneVars(tone: UploadTone): { bg: string; fg: string; border: string } {
  switch (tone) {
    case 'info':
      return { bg: 'var(--sb-info-bg)', fg: 'var(--sb-info-fg)', border: 'var(--sb-info-border)' };
    case 'active':
      return {
        bg: 'var(--sb-success-bg)',
        fg: 'var(--sb-success-fg)',
        border: 'var(--sb-success-border)',
      };
    case 'pending':
      return {
        bg: 'var(--sb-warning-bg)',
        fg: 'var(--sb-warning-fg)',
        border: 'var(--sb-warning-border)',
      };
    case 'rejected':
      return {
        bg: 'var(--sb-danger-bg)',
        fg: 'var(--sb-danger-fg)',
        border: 'var(--sb-danger-border)',
      };
    default:
      return { bg: 'var(--sb-neutral-100)', fg: 'var(--sb-text-muted)', border: 'var(--sb-border)' };
  }
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`;
  return `${Math.max(1, Math.round(bytes / 1e3))} KB`;
}

/**
 * Time remaining, or "Calculating…" until there is enough signal to be honest about it. A confident ETA produced
 * from one sample is worse than none — it swings wildly and the teacher stops trusting the number.
 */
export function formatEta(remainingBytes: number, bytesPerSecond: number | null): string {
  if (!bytesPerSecond || bytesPerSecond <= 0) return 'Calculating…';
  const seconds = Math.max(1, remainingBytes / bytesPerSecond);
  if (seconds < 60) return `${Math.round(seconds)} s left`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min left`;
  return `${(seconds / 3600).toFixed(1)} hr left`;
}

/** Where an upload row is being rendered; it changes which actions make sense. */
export type UploadRowContext = 'tray' | 'edit' | 'detail';

export type UploadRowAction =
  | 'pause'
  | 'resume'
  | 'resumeWithFile'
  | 'retry'
  | 'cancel'
  | 'dismiss'
  | 'moveUp'
  | 'moveDown'
  | 'openSession';

/**
 * Which controls a row offers — the prototype's `upActions` matrix, expressed over primitives.
 *
 * The rule that matters: only the browser holding the file can send bytes, so a colleague's stalled upload gets an
 * explanation instead of a Resume button that could never work (FR-ADM-UPL-012). A dead control is worse than no
 * control — the teacher clicks it, nothing happens, and they conclude the feature is broken.
 */
export function uploadRowActions(
  status: UploadStatusKey,
  mine: boolean,
  context: UploadRowContext,
): readonly UploadRowAction[] {
  const actions: UploadRowAction[] = [];

  if (context === 'tray' && status === 'Queued') actions.push('moveUp', 'moveDown');
  if (mine && status === 'Uploading') actions.push('pause');
  if (mine && status === 'Paused') actions.push('resume');
  if (mine && status === 'Interrupted') actions.push('resumeWithFile');
  if (mine && status === 'Failed') actions.push('retry');
  if (context === 'tray' && status === 'Ready') actions.push('openSession');
  if (status === 'Failed') actions.push('dismiss');

  // Any staff member with edit rights may cancel, including someone else's upload.
  if (status !== 'Ready' && status !== 'Failed') actions.push('cancel');

  return actions;
}

/**
 * The note shown in place of a Resume the viewer could never use.
 *
 * The server holds the partial object and the byte offset, but never the source file — that lives on the machine of
 * whoever picked it. So the wording says who can act and what this viewer can still do, rather than stating a
 * technical fact about browsers; the previous phrasing ("Only X's browser has this file") read as a glitch. The full
 * name is used because first-name truncation mangles titles like "Head Teacher".
 */
export function foreignUploadNote(
  status: UploadStatusKey,
  mine: boolean,
  startedByName: string,
): string | null {
  if (mine) return null;
  if (status !== 'Interrupted' && status !== 'Paused') return null;
  const who = startedByName.trim() || 'the person who started it';
  return `The file is on ${who}’s computer, so only they can resume it. You can cancel it here.`;
}

/** Progress-bar colour for a state. */
export function uploadBarVariant(status: UploadStatusKey): UploadStatusMeta['bar'] {
  return UPLOAD_STATUS_META[status].bar;
}

export interface UploadCaptionInput {
  readonly status: UploadStatusKey;
  readonly sentBytes: number;
  readonly totalBytes: number;
  readonly rate: number | null;
  readonly attempt: number;
  readonly maxAttempts: number;
  readonly percent: number;
  readonly sessionTitle: string;
  readonly queuedBehind: number;
  readonly error: string | null;
}

/**
 * The one-line explanation under each row. The wording matters more than it looks: `Interrupted` has to say the
 * progress is safe and what to do next, or the teacher assumes the upload is lost and starts again from zero.
 */
export function uploadCaption(input: UploadCaptionInput): string {
  const sent = formatBytes(input.sentBytes);
  const total = formatBytes(input.totalBytes);

  switch (input.status) {
    case 'Queued':
      return `Waiting behind ${input.queuedBehind} active upload${input.queuedBehind === 1 ? '' : 's'} · ${total}`;
    case 'Uploading':
      return `${sent} of ${total} · ${formatEta(input.totalBytes - input.sentBytes, input.rate)}`;
    case 'Retrying':
      return `Network dropped — retrying · attempt ${input.attempt} of ${input.maxAttempts} · ${input.percent}% held`;
    case 'Paused':
      return `Paused at ${sent} of ${total} · resumes straight away, no need to pick the file again`;
    case 'Interrupted':
      return `Stopped at ${input.percent}% — ${sent} is safe on the server. Pick the file again to carry on.`;
    case 'Finalising':
      return `All ${total} received · verifying checksum and assembling`;
    case 'Processing':
      return 'Bytes are in — transcoding to secure HLS';
    case 'Ready':
      return `Playable · now in ${input.sessionTitle}`;
    case 'Failed':
      return input.error ?? 'Something went wrong.';
    default:
      return '';
  }
}
